import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '../../config/api';
import { toast } from 'react-hot-toast';

// =====================================================
// INTERFACES
// =====================================================

interface Driver {
  id: string;
  driver_user_id: string;
  driver_email: string;
  driver_name: string;
  status: 'active' | 'inactive' | 'suspended';
  assigned_at: string;
  notes?: string;
  total_errands: number;
  completed_errands: number;
}

interface AvailableDriver {
  id: string;
  email: string;
  full_name: string;
  phone?: string;
  role: string;
  created_at: string;
}

interface DriverAssignmentProps {
  companyId: string;
  companyName: string;
}

// =====================================================
// DRIVER ASSIGNMENT COMPONENT
// =====================================================

const DriverAssignment: React.FC<DriverAssignmentProps> = ({ companyId, companyName }) => {
  const { user } = useAuth();
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [availableDrivers, setAvailableDrivers] = useState<AvailableDriver[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedDriverEmail, setSelectedDriverEmail] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive' | 'suspended'>('all');

  useEffect(() => {
    fetchData();
  }, [companyId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchCompanyDrivers(),
        fetchAvailableDrivers()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load driver data');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyDrivers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.BASE}/api/couriers/${companyId}/drivers`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch company drivers');
      }

      const data = await response.json();
      setDrivers(data.drivers || []);
    } catch (error) {
      console.error('Error fetching company drivers:', error);
      toast.error('Failed to load company drivers');
    }
  };

  const fetchAvailableDrivers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.BASE}/api/couriers/drivers/available`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch available drivers');
      }

      const data = await response.json();
      setAvailableDrivers(data.drivers || []);
    } catch (error) {
      console.error('Error fetching available drivers:', error);
      toast.error('Failed to load available drivers');
    }
  };

  const handleAssignDriver = async () => {
    if (!selectedDriverEmail) {
      toast.error('Please select a driver to assign');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.BASE}/api/couriers/${companyId}/drivers/assign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          driverEmail: selectedDriverEmail
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to assign driver');
      }

      const data = await response.json();
      toast.success(data.message || 'Driver assigned successfully');

      // Reset form and close dialog
      setSelectedDriverEmail('');
      setIsDialogOpen(false);

      // Refresh data
      fetchData();
    } catch (error) {
      console.error('Error assigning driver:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to assign driver');
    }
  };

  const handleUpdateDriverStatus = async (driverId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.BASE}/api/couriers/${companyId}/drivers/${driverId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status: newStatus
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update driver status');
      }

      const data = await response.json();
      toast.success(data.message);

      // Update local state
      setDrivers(prev => prev.map(driver =>
        driver.id === driverId
          ? { ...driver, status: newStatus as any }
          : driver
      ));
    } catch (error) {
      console.error('Error updating driver status:', error);
      toast.error('Failed to update driver status');
    }
  };

  const handleRemoveDriver = async (driverId: string, driverName: string) => {
    if (!confirm(`Are you sure you want to remove ${driverName} from the company?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.BASE}/api/couriers/${companyId}/drivers/${driverId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to remove driver');
      }

      const data = await response.json();
      toast.success(data.message);

      // Refresh data
      fetchData();
    } catch (error) {
      console.error('Error removing driver:', error);
      toast.error('Failed to remove driver');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      case 'suspended':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return '✅';
      case 'inactive':
        return '⏸️';
      case 'suspended':
        return '🚫';
      default:
        return '❓';
    }
  };

  const filteredDrivers = drivers.filter(driver => {
    const matchesSearch =
      driver.driver_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      driver.driver_email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || driver.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const unassignedDrivers = availableDrivers.filter(driver =>
    !drivers.some(assignedDriver => assignedDriver.driver_email === driver.email)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-primary-800">Driver Management</h2>
          <p className="text-primary-600 mt-1">
            Manage drivers for {companyName}
          </p>
        </div>
        <button
          onClick={() => setIsDialogOpen(true)}
          className="btn-primary flex items-center gap-2"
        >
          <span>➕</span>
          Assign Driver
        </button>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <div className="card-primary">
          <div className="flex items-center">
            <div className="p-2 bg-primary-100 rounded-lg">
              <span className="text-primary-600 text-xl">👥</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-primary-600">Total Drivers</p>
              <p className="text-2xl font-semibold text-primary-800">{drivers.length}</p>
            </div>
          </div>
        </div>

        <div className="card-secondary">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <span className="text-green-600 text-xl">✅</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-secondary-600">Active</p>
              <p className="text-2xl font-semibold text-secondary-800">
                {drivers.filter(d => d.status === 'active').length}
              </p>
            </div>
          </div>
        </div>

        <div className="card-tertiary">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <span className="text-yellow-600 text-xl">⏸️</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-tertiary-600">Inactive</p>
              <p className="text-2xl font-semibold text-tertiary-800">
                {drivers.filter(d => d.status === 'inactive').length}
              </p>
            </div>
          </div>
        </div>

        <div className="card-neutral">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <span className="text-blue-600 text-xl">📋</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Available</p>
              <p className="text-2xl font-semibold text-gray-800">
                {unassignedDrivers.length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card-highlighted">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search drivers by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field w-full"
            />
          </div>
          <div className="flex gap-2">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as any)}
              className="input-field"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
        </div>
      </div>

      {/* Drivers List */}
      <div className="card-highlighted">
        <h3 className="text-lg font-semibold text-primary-800 mb-4">Assigned Drivers</h3>
        {filteredDrivers.length === 0 ? (
          <div className="text-center py-8">
            <span className="text-4xl mb-4 block">👥</span>
            <p className="text-primary-500">No drivers found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredDrivers.map(driver => (
              <div
                key={driver.id}
                className="flex items-center justify-between p-4 border border-primary-200 rounded-lg hover:bg-primary-50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{getStatusIcon(driver.status)}</span>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(driver.status)}`}>
                      {driver.status}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-primary-900">{driver.driver_name}</p>
                    <p className="text-sm text-primary-500">{driver.driver_email}</p>
                    <p className="text-xs text-primary-400">
                      Assigned: {new Date(driver.assigned_at).toLocaleDateString()} •
                      Errands: {driver.completed_errands}/{driver.total_errands}
                    </p>
                    {driver.notes && (
                      <p className="text-xs text-primary-400 mt-1">Note: {driver.notes}</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleUpdateDriverStatus(driver.id, driver.status)}
                    className={`text-xs px-3 py-1 rounded ${
                      driver.status === 'active'
                        ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                        : 'bg-green-100 text-green-800 hover:bg-green-200'
                    }`}
                  >
                    {driver.status === 'active' ? 'Deactivate' : 'Activate'}
                  </button>
                  <button
                    onClick={() => handleRemoveDriver(driver.id, driver.driver_name)}
                    className="text-xs px-3 py-1 rounded bg-red-100 text-red-800 hover:bg-red-200"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Available Drivers */}
      {unassignedDrivers.length > 0 && (
        <div className="card-highlighted">
          <h3 className="text-lg font-semibold text-primary-800 mb-4">Available Drivers</h3>
          <div className="space-y-2">
            {unassignedDrivers.map(driver => (
              <div
                key={driver.id}
                className="flex items-center justify-between p-3 border border-primary-200 rounded-lg"
              >
                <div>
                  <p className="font-medium text-primary-900">{driver.full_name}</p>
                  <p className="text-sm text-primary-500">{driver.email}</p>
                  {driver.phone && (
                    <p className="text-sm text-primary-500">{driver.phone}</p>
                  )}
                </div>
                <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-800">
                  {driver.role}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Assign Driver Modal */}
      {isDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-primary-800 mb-4">
              Assign Driver to {companyName}
            </h3>

            <div className="mb-4">
              <label className="block text-sm font-medium text-primary-700 mb-2">
                Select Driver
              </label>
              <select
                value={selectedDriverEmail}
                onChange={(e) => setSelectedDriverEmail(e.target.value)}
                className="input-field w-full"
              >
                <option value="">Choose a driver...</option>
                {unassignedDrivers.map(driver => (
                  <option key={driver.id} value={driver.email}>
                    {driver.full_name} - {driver.email}
                  </option>
                ))}
              </select>
            </div>

            {unassignedDrivers.length === 0 && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  No available drivers to assign. All registered users are already assigned to companies.
                </p>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setIsDialogOpen(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleAssignDriver}
                disabled={!selectedDriverEmail || unassignedDrivers.length === 0}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Assign Driver
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverAssignment;
